import { arrayMove } from '@dnd-kit/sortable'

interface Ordered {
  id: number
  order: number
}

// Deterministic display order: by `order`, ties broken by `id`. The backend
// does not guarantee a stable order for duplicate `order` values, so relying
// on response order shuffles blocks between refetches.
export const compareByOrder = (a: Ordered, b: Ordered): number => a.order - b.order || a.id - b.id

export function sortByOrder<T extends Ordered>(items: readonly T[]): T[] {
  return [...items].sort(compareByOrder)
}

// Moves items[fromIndex] to toIndex and renumbers the whole list as 0..n-1.
// Returns only the items whose `order` changed — renumbering also heals
// pre-existing duplicates and gaps.
export function computeReorderUpdates<T extends Ordered>(
  items: readonly T[],
  fromIndex: number,
  toIndex: number,
): Array<{ item: T; order: number }> {
  return arrayMove([...items], fromIndex, toIndex)
    .map((item, index) => ({ item, order: index }))
    .filter((u) => u.item.order !== u.order)
}

// Applies new orders to a cached list in place (RTK Query optimistic draft).
export function applyOrderUpdates(
  items: Ordered[],
  updates: ReadonlyArray<{ id: number; order: number }>,
): void {
  const orderById = new Map(updates.map((u) => [u.id, u.order]))
  for (const item of items) {
    const order = orderById.get(item.id)
    if (order !== undefined) item.order = order
  }
  items.sort(compareByOrder)
}
